import { useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import EToolTip from 'components/others/EToolTip';
import SolarTuningLineDuotone from 'components/icons/SolarTuningLineDuotone';
import OffVendors from './OffVendors';
import OffImport, { type ProductImportMode } from './OffImport';
import { useProduct } from './context/ProductProvider';
import SolarIcon from 'components/icons/SolarIcon';

const ProductAdminDropdown = () => {
    const { t } = useTranslation(['Products', 'common']);
    const { triggerListRefresh } = useProduct();
    const [showVendorsEditor, setShowVendorsEditor] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [importMode, setImportMode] = useState<ProductImportMode | null>(null);

    const openImport = (mode: ProductImportMode) => {
        setImportMode(mode);
        setShowImport(true);
    };

    const handleImportClose = (show: boolean) => {
        setShowImport(show);
        if (!show) setImportMode(null);
    };

    const renderMenuItem = (
        icon: React.ReactNode,
        title: string,
        subtitle: string,
        onSelect: () => void,
        className = '',
    ) => (
        <Dropdown.Item
            as="button"
            className={className}
            onClick={(e) => {
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon">
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className="fw-medium">{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {renderMenuItem(
                <SolarIcon name="truck" />,
                t('productAdmin.vendors'),
                t('productAdmin.vendorsHint'),
                () => setShowVendorsEditor(true),
            )}

            <Dropdown.Divider className="my-2" />

            {renderMenuItem(
                <SolarIcon name="filetype-csv" />,
                t('productAdmin.importCsv'),
                t('productAdmin.importCsvHint'),
                () => openImport('csv'),
            )}

            {renderMenuItem(
                <SolarIcon name="stars" />,
                t('productAdmin.importIa'),
                t('productAdmin.importIaHint'),
                () => openImport('ia'),
                'product-admin-ai-item',
            )}
        </Dropdown.Menu>
    );

    return (
        <>
            <EToolTip title={t('productAdmin.menuTitle')}>
                <span className="d-inline-block ms-1">
                    <Dropdown as="span" align="end" className="d-inline-flex">
                        <Dropdown.Toggle
                            as={Button}
                            size="sm"
                            variant="secondary"
                            className="toolbar-menu-btn fs-14 d-inline-flex align-items-center"
                        >
                            <SolarTuningLineDuotone />
                        </Dropdown.Toggle>

                        {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
                    </Dropdown>
                </span>
            </EToolTip>

            <OffVendors show={showVendorsEditor} setShow={setShowVendorsEditor} />
            <OffImport show={showImport} setShow={handleImportClose} mode={importMode} onImported={triggerListRefresh} />
        </>
    );
};

export default ProductAdminDropdown;
